import { useRef } from 'react';

import { useHouseholdTeamNameResolver } from '@/views/admin/tenantTeam/tenant-team-queries';
import { useGlobalAccount } from '@/views/household/household-queries';
import {
    HouseholdNoteForm,
    HouseholdNoteFormRef,
    HouseholdNoteFormType,
} from '@/views/household/HouseholdNotes/HouseholdNoteForm';
import { useNoteQuery, useUpdateNoteMutation } from '@/views/household/HouseholdNotes/note-queries';
import { getNotePersonName } from '@/views/household/HouseholdNotes/note-utils';
import { NoteBody } from '@/views/household/HouseholdNotes/NoteBodyCard';
import { TextLink } from '@/widgets';
import { Toast } from '@/widgets/Toast';
import { getHouseholdName } from 'utils';

import { useDockState } from '../dock-state';
import { UpdateNoteDockItem } from '../dock-state-types';

// One UPDATE_NOTE dock item, two bodies: 'view' shows the fresh read-only note,
// absent/'edit' keeps the historical edit-form pin flows. Splitting the bodies keeps
// the note fetch out of the edit path, which works off the seeded form values only.
export const NoteDockItem = (props: { item: UpdateNoteDockItem }) => {
    const viewMode = props.item.data.viewMode ?? 'edit';
    return viewMode === 'view' ? <NoteDockItemView item={props.item} /> : <NoteDockItemEdit item={props.item} />;
};

const NoteDockItemView = ({ item }: { item: UpdateNoteDockItem }) => {
    const noteQuery = useNoteQuery(item.data.noteGUID);
    const { data: globalAccount } = useGlobalAccount(item.data.globalAccountGUID);
    const { nameFor } = useHouseholdTeamNameResolver(item.data.globalAccountGUID);

    if (noteQuery.isLoading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Loading note details...</div>
            </div>
        );
    }

    const note = noteQuery.data;
    if (!note) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-muted-foreground">Note not found.</div>
            </div>
        );
    }

    const personName = getNotePersonName(note.createdBy, nameFor);
    const updatedPersonName = note.updatedBy ? getNotePersonName(note.updatedBy, nameFor) : personName;

    return (
        <div className="flex flex-col gap-3">
            {globalAccount && (
                <div className="flex items-center gap-2 text-sm">
                    <span className="text-muted-foreground">Household:</span>
                    <TextLink to={`/household/${globalAccount.globalAccountGUID}`}>
                        {getHouseholdName(globalAccount) || 'Unnamed Household'}
                    </TextLink>
                </div>
            )}
            <NoteBody
                note={note}
                personName={personName}
                updatedPersonName={updatedPersonName}
                editorContentClassName="note-dock-view"
            />
        </div>
    );
};

const NoteDockItemEdit = ({ item }: { item: UpdateNoteDockItem }) => {
    const { setActiveId, updateDockItem, removeDockItem } = useDockState();
    const formRef = useRef<HouseholdNoteFormRef>(null);
    const { data: globalAccount } = useGlobalAccount(item.data.globalAccountGUID);
    const updateNoteMutation = useUpdateNoteMutation(item.data.globalAccountGUID ?? '', item.data.noteGUID);

    const householdOption =
        item.data.householdOption ??
        (globalAccount
            ? {
                  value: item.data.globalAccountGUID ?? '',
                  label: getHouseholdName(globalAccount) || 'Unnamed Household',
              }
            : undefined);

    return (
        <div
            onBlur={() => {
                const formValues = formRef.current?.formMethods.getValues();
                if (formValues) {
                    updateDockItem({
                        ...item,
                        data: {
                            ...item.data,
                            subject: formValues?.subject || '',
                            content: formValues?.content || '',
                            householdOption: formValues?.householdOption,
                        },
                    });
                }
            }}
        >
            <HouseholdNoteForm
                key={item.id}
                ref={formRef}
                globalAccount={globalAccount}
                onSubmit={(data: HouseholdNoteFormType) => {
                    if (updateNoteMutation.isPending) return;
                    updateNoteMutation
                        .mutateAsync({
                            subject: data.subject,
                            content: data.content,
                            noteGUID: item.data.noteGUID,
                        })
                        .then(() => {
                            Toast.success('Note updated successfully');
                            removeDockItem(item.id);
                        })
                        .catch((error) => {
                            Toast.forError(error, 'Failed to update note');
                        });
                }}
                defaultValues={{
                    subject: item.data.subject,
                    content: item.data.content,
                    householdOption,
                }}
                onCancel={() => setActiveId(null)}
                showHouseholdInput={false}
            />
        </div>
    );
};
